import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static values = { url: String }

  connect() {
    this._prefetch = this.#fetch()
    this.#observe()
  }

  disconnect() {
    this._observer?.disconnect()
  }

  // A bounded, internally-scrolling table wrapper marks itself as the root
  // to observe against instead of the viewport — see data_table.scss's
  // .data-table--orders .data-table__wrapper.
  #root() {
    return this.element.closest('[data-sentinel-root]')
  }

  #observe() {
    const root = this.#root()
    this._observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) this.#render() },
      { root, rootMargin: root ? '200px' : '500px' }
    )
    this._observer.observe(this.element)
  }

  #fetch() {
    return fetch(this.urlValue, {
      headers: { Accept: 'text/vnd.turbo-stream.html' }
    })
  }

  // The current URL's #fragment target, if it points to an element on this
  // page. location.hash can be a non-selector value (e.g. a bare "#"),
  // which throws in querySelector, so this can't just be inlined.
  #hashTarget() {
    if (!location.hash) return null
    try {
      return document.querySelector(location.hash)
    } catch {
      return null
    }
  }

  async #render() {
    this._observer.disconnect()

    // Keep whatever the user is anchored to in place while new rows load
    // above it: a URL fragment target if that's how they got here, otherwise
    // whatever's pinned at the viewport's top edge. Only relevant for
    // whole-page scroll — a bounded table's own scroll position is already
    // left untouched by appending rows below it, so there's nothing to pin.
    const root = this.#root()
    const hashTarget = root ? null : this.#hashTarget()
    const scrolledPast = !root && this.element.getBoundingClientRect().bottom < 0
    const pin = hashTarget ?? (scrolledPast ? document.elementFromPoint(window.innerWidth / 2, 0) : null)
    const pinTop = pin?.getBoundingClientRect().top

    const response = await this._prefetch
    if (!response.ok || !this.element.isConnected) return
    const html = await response.text()

    // Suppress native scroll-anchoring so it doesn't double-compensate on
    // top of our own correction below.
    if (pin) document.documentElement.style.overflowAnchor = 'none'
    Turbo.renderStreamMessage(html)

    if (pin) {
      if (hashTarget) pin.scrollIntoView()
      else window.scrollBy(0, pin.getBoundingClientRect().top - pinTop)
      requestAnimationFrame(() => {
        document.documentElement.style.overflowAnchor = ''
      })
    }
  }
}
